/**
 * Shared types for the email composer recipients panel.
 *
 * The panel is provider-agnostic: it surfaces a list of contacts, lets users
 * assign them to TO/CC/BCC roles, supports ad-hoc custom recipients, and
 * optionally renders a "Send From" selector. The actual outbox payload
 * (e.g. SendFromOverride in AR statements) is opaque to this panel — callers
 * pass `{ key, label }` options and look up the resolved value on their side.
 */

import { z } from 'zod';

export type RecipientRole = 'to' | 'cc' | 'bcc';

export interface CustomRecipient {
  id: string;
  email: string;
  role: RecipientRole;
}

export interface RecipientChipDescriptor {
  key: string;
  email: string;
  onRemove: () => void;
}

export interface EmailComposerContact {
  id: string;
  email: string;
  tags: string[];
}

export interface SendFromOption {
  /** Stable identifier used as the Select value. */
  key: string;
  label: string;
}

let recipientIdCounter = 0;
export function makeRecipientId(): string {
  recipientIdCounter += 1;
  return `cr-${recipientIdCounter}`;
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validates against the same rule the send APIs enforce: z.email() is the zod
// v4 canonical form of the contract's z.string().email(). Client-side gating
// that decides whether an address may be forwarded to a send/email endpoint
// (e.g. statement BCC recipients) MUST use this, not the looser EMAIL_REGEX —
// otherwise a value can pass the client check and still be 400-rejected server
// side. EMAIL_REGEX remains fine for purely-cosmetic UX filtering.
const sendableEmailSchema = z.email();
export function isSendableEmail(value: string): boolean {
  return sendableEmailSchema.safeParse(value.trim()).success;
}

/**
 * Collapses raw contact records (e.g. from useArCustomerContacts) down to one
 * row per distinct email address, merging tags from any duplicates and
 * dropping rows without a usable email.
 */
export function dedupeContactsByEmail(
  contacts: Array<{
    id: string;
    email: string | null;
    tags: Array<{ name: string }>;
  }>,
): EmailComposerContact[] {
  const map = new Map<string, EmailComposerContact>();
  for (const c of contacts) {
    if (!c.email || !EMAIL_REGEX.test(c.email.trim())) continue;
    const key = c.email.trim().toLowerCase();
    const existing = map.get(key);
    const tagNames = c.tags
      .map((t) => t.name.trim())
      .filter((n) => n.length > 0);
    if (existing) {
      for (const t of tagNames) {
        if (!existing.tags.includes(t)) existing.tags.push(t);
      }
    } else {
      map.set(key, {
        id: c.id,
        email: key,
        tags: Array.from(new Set(tagNames)),
      });
    }
  }
  return Array.from(map.values());
}
